import React, { useContext, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import {
  DashboardLayout,
  MainContent,
  Sidebar,
} from 'components/DashboardLayout';
import { DetailRow, DetailLabel, DetailValue } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Toggle from 'components/FormFields/Toggle';
import { DeleteConfirmModal } from 'components/FormModal';
import PageContainer from 'components/PageContainer';
import { HeaderContainer, HeaderTitle } from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import QuickActions from 'components/QuickActions';
import Toaster from 'components/Toaster';
import { logDevError } from 'shared/alerts';
import { AppContext } from 'shared/AppContext';
import { formatPhoneNumber } from 'shared/formatters';
import GuestFormModal from '../GuestList/GuestListEvents/components/GuestFormModal';
import PermissionModal from '../PermissionModal/PermissionModal';
import {
  GET_GUESTS,
  useCheckinGuest,
  useDeleteGuest,
  useGetGuest,
  useUnCheckGuest,
} from './gql';
import { withGuestSearchContext } from './GuestSearch';

const GuestTypeBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) =>
    props.type === 'year'
      ? props.theme.name === 'dark'
        ? 'rgba(99, 102, 241, 0.2)'
        : 'rgba(99, 102, 241, 0.1)'
      : props.theme.name === 'dark'
      ? 'rgba(34, 197, 94, 0.2)'
      : 'rgba(34, 197, 94, 0.1)'};
  color: ${(props) => (props.type === 'year' ? '#6366F1' : '#22C55E')};
`;

const CheckInRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  margin-bottom: 8px;
`;

const CheckInLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
`;

const CheckedStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) =>
    props.checked
      ? props.theme.name === 'dark'
        ? 'rgba(34, 197, 94, 0.2)'
        : 'rgba(34, 197, 94, 0.1)'
      : props.theme.name === 'dark'
      ? 'rgba(148, 163, 184, 0.2)'
      : 'rgba(148, 163, 184, 0.1)'};
  color: ${(props) => (props.checked ? '#22C55E' : '#94A3B8')};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
`;

// Skeleton loading styles
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SkeletonBase = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.06) 75%)'
      : 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)'};
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 4px;
`;

const SkeletonDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
`;

const SkeletonLabel = styled(SkeletonBase)`
  width: 120px;
  height: 16px;
`;

const SkeletonValue = styled(SkeletonBase)`
  width: ${(props) => props.width || '180px'};
  height: 16px;
`;

const SkeletonBadge = styled(SkeletonBase)`
  width: 100px;
  height: 28px;
  border-radius: 12px;
`;

const SkeletonButton = styled(SkeletonBase)`
  width: 120px;
  height: 40px;
  border-radius: 8px;
`;

const SkeletonToggle = styled(SkeletonBase)`
  width: 48px;
  height: 24px;
  border-radius: 12px;
`;

const SkeletonQuickAction = styled(SkeletonBase)`
  width: 100%;
  height: 44px;
  border-radius: 8px;
  margin-bottom: 8px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
`;

const TrackGuestDetail = () => {
  const history = useHistory();
  const { pathname, state } = useLocation();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [toasterData, setToasterData] = useState(null);

  // Determine route prefix based on current path
  const isTrackAdmin = pathname.includes('/admin-track');
  const isEmployeeAdmin = pathname.includes('/admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const userType = isEmployeeAdmin ? 'employee' : 'track';

  const {
    state: { capabilities },
  } = useContext(AppContext);

  const { data, loading, refetch } = useGetGuest();
  const deleteGuest = useDeleteGuest();
  const checkinGuest = useCheckinGuest();
  const unCheckGuest = useUnCheckGuest();

  const guest = data?.getGuest;

  // Check if we're on the guest-lists event detail page (not the /guests page)
  const shouldRenderCheckin = !pathname.includes('/guests/');

  const handleCheckInToggle = async () => {
    if (!capabilities || !capabilities?.check_in_guests) {
      setShowPermissionModal(true);
      return;
    }

    setIsCheckingIn(true);
    try {
      if (guest.is_checked) {
        await unCheckGuest(guest.id);
        setToasterData({
          title: 'Success',
          message: 'Guest unchecked successfully',
          type: 'success',
        });
      } else {
        await checkinGuest(guest.id);
        setToasterData({
          title: 'Success',
          message: 'Guest checked in successfully',
          type: 'success',
        });
      }
      refetch();
    } catch (error) {
      logDevError(error);
      setToasterData({
        title: 'Error',
        message: 'Error updating check-in status',
        type: 'error',
      });
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  // Check if we're on the event guest list page (not the /guests page)
  const isOnEventGuestListPage = pathname.includes('/guest-lists/event/');
  // Extract eventId from pathname for navigation back to event guest list
  const eventIdFromPath = pathname.match(/\/guest-lists\/event\/(\d+)\//)?.[1];

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteGuest({
        variables: { id: guest.id },
        update: (cache) => {
          const cachedData = cache.readQuery({
            query: GET_GUESTS,
            variables: { input: { queryString: state?.queryString ?? '' } },
          });
          if (cachedData) {
            const updatedResults = cachedData.getGuestsV2.results.filter(
              (g) => g.id !== guest.id
            );
            cache.writeQuery({
              query: GET_GUESTS,
              variables: { input: { queryString: state?.queryString ?? '' } },
              data: {
                ...cachedData,
                getGuestsV2: {
                  ...cachedData.getGuestsV2,
                  count: cachedData.getGuestsV2.count - 1,
                  results: updatedResults,
                },
              },
            });
          }
        },
      });

      // Store success message for the destination page to display
      sessionStorage.setItem('guestSuccessMessage', 'Guest deleted successfully');

      // Navigate back appropriately based on context
      if (isOnEventGuestListPage && eventIdFromPath) {
        // Use replace to force remount and fresh data fetch
        history.replace(`${routePrefix}/guest-lists/event/${eventIdFromPath}`);
      } else {
        history.push(`${routePrefix}/guests`);
      }
    } catch (error) {
      logDevError(error);
      // Store error message for display
      sessionStorage.setItem('guestErrorMessage', 'Error deleting guest');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleGuestUpdated = () => {
    refetch();
  };

  if (loading) {
    return (
      <EnhancedThemeProvider>
        <PageContainer>
          {/* Header Card */}
          <Card style={{ marginBottom: '30px' }}>
            <HeaderContainer>
              <PageHeaderIcon icon="users" />
              <HeaderTitle>Guest Details</HeaderTitle>
            </HeaderContainer>
          </Card>

          {/* Dashboard Layout with Skeletons */}
          <DashboardLayout>
            <MainContent>
              <Card noPadding>
                <ContentWrapper>
                  {/* Check-in row skeleton (for event guest list pages) */}
                  {shouldRenderCheckin && (
                    <CheckInRow>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <SkeletonLabel />
                        <SkeletonBadge />
                      </div>
                      <SkeletonToggle />
                    </CheckInRow>
                  )}

                  {/* Detail row skeletons */}
                  {[...Array(7)].map((_, index) => (
                    <SkeletonDetailRow key={index}>
                      <SkeletonLabel />
                      <SkeletonValue width={index === 2 ? '100px' : index === 6 ? '250px' : '180px'} />
                    </SkeletonDetailRow>
                  ))}

                  {/* Action buttons skeleton */}
                  {userType !== 'employee' && (
                    <ActionButtons>
                      <SkeletonButton />
                      <SkeletonButton />
                    </ActionButtons>
                  )}
                </ContentWrapper>
              </Card>
            </MainContent>

            <Sidebar>
              {/* Quick Actions skeleton */}
              <Card>
                <div style={{ padding: '16px' }}>
                  <SkeletonValue width="120px" style={{ marginBottom: '16px' }} />
                  <SkeletonQuickAction />
                  <SkeletonQuickAction />
                </div>
              </Card>
            </Sidebar>
          </DashboardLayout>
        </PageContainer>
      </EnhancedThemeProvider>
    );
  }

  if (!guest) {
    return (
      <EnhancedThemeProvider>
        <PageContainer>
          <Card style={{ marginBottom: '30px' }}>
            <HeaderContainer>
              <PageHeaderIcon icon="users" />
              <HeaderTitle>Guest Details</HeaderTitle>
            </HeaderContainer>
          </Card>
          <Card>
            <ContentWrapper>
              <LoadingContainer>Guest not found</LoadingContainer>
            </ContentWrapper>
          </Card>
        </PageContainer>
      </EnhancedThemeProvider>
    );
  }

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="users" />
            <HeaderTitle>Guest Details</HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Content Card */}
            <Card noPadding>
              <ContentWrapper>
                {/* Check-in toggle for event guest list pages */}
                {shouldRenderCheckin && (
                  <CheckInRow>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      <CheckInLabel>Check In Status:</CheckInLabel>
                      <CheckedStatus checked={guest.is_checked}>
                        {guest.is_checked ? 'Checked In' : 'Not Checked In'}
                      </CheckedStatus>
                    </div>
                    <Toggle
                      checked={!!guest.is_checked}
                      onChange={handleCheckInToggle}
                      disabled={isCheckingIn}
                    />
                  </CheckInRow>
                )}

                {/* Guest Details */}
                <DetailRow>
                  <DetailLabel>Name</DetailLabel>
                  <DetailValue>{`${guest.last_name}, ${guest.first_name}`}</DetailValue>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Phone Number</DetailLabel>
                  <DetailValue>
                    {formatPhoneNumber(guest.phone_number)}
                  </DetailValue>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Guest Type</DetailLabel>
                  <DetailValue>
                    {guest.year ? (
                      <GuestTypeBadge type="year">Full Season</GuestTypeBadge>
                    ) : guest.event_id ? (
                      <GuestTypeBadge type="event">Single Event</GuestTypeBadge>
                    ) : (
                      '-'
                    )}
                  </DetailValue>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Year</DetailLabel>
                  <DetailValue>{guest.year || '-'}</DetailValue>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Event / Track</DetailLabel>
                  <DetailValue>
                    {guest.event?.name || guest.ownership?.label || '-'}
                  </DetailValue>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Additional Guests</DetailLabel>
                  <DetailValue>
                    {guest.additional_guests
                      ? `+${guest.additional_guests}`
                      : '0'}
                  </DetailValue>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Notes</DetailLabel>
                  <DetailValue>{guest.notes || '-'}</DetailValue>
                </DetailRow>

                {/* Action Buttons */}
                {userType !== 'employee' && (
                  <ActionButtons>
                    <ButtonV2 onClick={handleEditClick}>Edit Guest</ButtonV2>
                    <ButtonV2 danger onClick={handleDeleteClick}>
                      Delete Guest
                    </ButtonV2>
                  </ActionButtons>
                )}
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            {/* Quick Actions */}
            <QuickActions
              title="Quick Actions"
              actions={[
                {
                  label: 'Back to Guests',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fillRule="evenodd"
                        d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"
                      />
                    </svg>
                  ),
                  onClick: () => history.goBack(),
                },
                {
                  label: 'View Guest Lists',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1h8zm-7.978-1A.261.261 0 0 1 7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72l-.008.002a.274.274 0 0 1-.014.002H7.022zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm3-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM6.936 9.28a5.88 5.88 0 0 0-1.23-.247A7.35 7.35 0 0 0 5 9c-4 0-5 3-5 4 0 .667.333 1 1 1h4.216A2.238 2.238 0 0 1 5 13c0-1.01.377-2.042 1.09-2.904.243-.294.526-.569.846-.816zM4.92 10A5.493 5.493 0 0 0 4 13H1c0-.26.164-1.03.76-1.724.545-.636 1.492-1.256 3.16-1.275zM1.5 5.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0zm3-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
                    </svg>
                  ),
                  onClick: () => history.push(`${routePrefix}/guest-lists`),
                },
              ]}
            />
          </Sidebar>
        </DashboardLayout>

        {/* Edit Guest Modal */}
        <GuestFormModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          guestId={guest.id}
          onGuestCreated={handleGuestUpdated}
          onSuccess={(message) => {
            setToasterData({
              title: 'Success',
              message,
              type: 'success',
            });
            refetch();
          }}
          onError={(message) =>
            setToasterData({
              title: 'Error',
              message,
              type: 'error',
            })
          }
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Guest"
          message={`Are you sure you want to delete ${guest.first_name} ${guest.last_name}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
        />

        {/* Permission Modal */}
        <PermissionModal
          isVisible={showPermissionModal}
          hideModal={() => setShowPermissionModal(false)}
          link={`${routePrefix}/settings/1`}
        />

        {/* Toaster for notifications */}
        <Toaster
          isOpen={!!toasterData}
          title={toasterData?.title || ''}
          message={toasterData?.message}
          type={toasterData?.type}
          onClose={() => setToasterData(null)}
        />
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default withGuestSearchContext(TrackGuestDetail);
